import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import {
  getShortcodeData,
  updateShortcodeSettings,
  saveShortcodeFlow,
  saveShortcodeKeywords,
  getShortcodeStats,
  exportShortcodeData,
  clearApiMessage,
  clearError,
  clearShortcodeState,
  selectShortcode,
  selectShortcodeFlows,
  selectShortcodeKeywords,
  selectShortcodeStats,
  selectShortcodeLoading,
  selectShortcodeSubmitting,
  selectShortcodeApiMessage,
  selectShortcodeError,
} from '../../store/slices/system/shortcodeSlice';
import {
  Pencil,
  AlertTriangle,
  Save,
  Plus,
  Trash2,
  Eye,
  Download,
  Smartphone,
  FileText,
  RefreshCw,
  CheckCircle2,
  Loader2,
  Pause,
  Play,
} from 'lucide-react';

const ShortcodeSetup = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  
  // ==================== STATE ====================
  const [flowSteps, setFlowSteps] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [shortcodeNumber, setShortcodeNumber] = useState('40404');
  const [registrationKeyword, setRegistrationKeyword] = useState('JOIN');
  const [isFlowActive, setIsFlowActive] = useState(true);
  const [editingStepId, setEditingStepId] = useState(null);
  const [editingKeywordId, setEditingKeywordId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // ==================== REFS ====================
  const shortcodeNumberRef = useRef(null);
  const shortcodeKeywordRef = useRef(null);

  const stepMsgRef = useRef(null);
  const stepFieldRef = useRef(null);
  const stepDelayRef = useRef(null);
  const stepRequiredRef = useRef(null);

  const keywordInputRef = useRef(null);
  const keywordDescRef = useRef(null);
  const keywordActiveRef = useRef(null);

  // Track last toast message
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const shortcode = useSelector(selectShortcode);
  const storedFlows = useSelector(selectShortcodeFlows);
  const storedKeywords = useSelector(selectShortcodeKeywords);
  const stats = useSelector(selectShortcodeStats);
  const loading = useSelector(selectShortcodeLoading);
  const isSubmitting = useSelector(selectShortcodeSubmitting);
  const apiMessage = useSelector(selectShortcodeApiMessage);
  const error = useSelector(selectShortcodeError);

  // ==================== EFFECTS ====================

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(getShortcodeData());
        await dispatch(getShortcodeStats());
      } catch (err) {
        console.error('Error loading shortcode data:', err);
      }
    };
    
    loadData();
    
    return () => {
      dispatch(clearShortcodeState());
    };
  }, [dispatch]);

  // Sync data from Redux to local state
  useEffect(() => {
    if (shortcode) {
      setShortcodeNumber(shortcode.shortcode_number || '40404');
      setRegistrationKeyword(shortcode.registration_keyword || 'JOIN');
      setIsFlowActive(shortcode.is_active !== false);
    }
    
    if (storedFlows.length > 0) {
      setFlowSteps(storedFlows.map(flow => ({
        id: flow.id,
        step: flow.step_number || flow.step || flow.id,
        msg: flow.message,
        field: flow.field_name || 'Custom field',
        required: flow.is_required !== false,
        delay: flow.delay_seconds || 0,
        is_active: flow.is_active !== false
      })));
    }
    
    if (storedKeywords.length > 0) {
      setKeywords(storedKeywords.map(kw => ({
        id: kw.id,
        keyword: kw.keyword,
        description: kw.description,
        color: kw.color || 'var(--blue-bg)',
        textColor: kw.text_color || 'var(--blue)',
        active: kw.is_active !== false,
        action_type: kw.action_type || 'reply',
        response_message: kw.response_message || null
      })));
    }
    
    setIsInitialized(true);
  }, [shortcode, storedFlows, storedKeywords]);

  // Display API messages
  useEffect(() => {
    if (!apiMessage || !showToast) return;
    
    const messageKey = `${apiMessage}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    
    if (lastToastRef.current.message === messageKey && 
        (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }
    
    lastToastRef.current = { message: messageKey, timestamp: now };
    
    const toastType = error ? 'error' : 'success';
    showToast(apiMessage, toastType);
    
    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== SHORTCODE SETTINGS ====================

  const handleEditShortcode = () => {
    setTimeout(() => {
      if (shortcodeNumberRef.current) shortcodeNumberRef.current.value = shortcodeNumber;
      if (shortcodeKeywordRef.current) shortcodeKeywordRef.current.value = registrationKeyword;
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit Shortcode Settings</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Shortcode Number</label>
            <input 
              className="form-input" 
              ref={shortcodeNumberRef}
              defaultValue=""
              placeholder="e.g. 40404"
            />
            <div className="form-hint">The number users will text to interact with your church</div>
          </div>
          <div className="form-group">
            <label className="form-label">Registration Keyword</label>
            <input 
              className="form-input" 
              ref={shortcodeKeywordRef}
              defaultValue=""
              placeholder="e.g. JOIN"
            />
            <div className="form-hint">The keyword users text to start registration</div>
          </div>
          <div style={{ 
            padding: '10px 12px', 
            background: 'var(--amber-bg)', 
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--amber)'
          }}>
<AlertTriangle size={16} style={{ verticalAlign: '-3px' }} /> Changing these settings will affect how new members register via SMS.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const number = shortcodeNumberRef.current?.value?.trim() || '';
              const keyword = shortcodeKeywordRef.current?.value?.trim() || '';
              
              if (!number) {
                showToastMessage('Please enter a shortcode number', 'error');
                return;
              }
              if (!keyword) {
                showToastMessage('Please enter a registration keyword', 'error');
                return;
              }
              
              try {
                const result = await dispatch(updateShortcodeSettings({
                  shortcode_number: number,
                  registration_keyword: keyword.toUpperCase()
                })).unwrap();
                
                if (result.success) {
                  setShortcodeNumber(number);
                  setRegistrationKeyword(keyword.toUpperCase());
                  showToastMessage('✅ Shortcode settings updated!', 'success');
                  closeModal();
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to update shortcode settings', 'error');
              }
            }}
          >
            {isSubmitting ? 'Saving...' : <><Save size={16} style={{ verticalAlign: '-3px' }} /> Save Changes</>}
          </button>
        </div>
      </div>
    );
  };

  // ==================== FLOW STEPS ====================

  const handleAddStep = () => {
    setTimeout(() => {
      if (stepMsgRef.current) stepMsgRef.current.value = '';
      if (stepFieldRef.current) stepFieldRef.current.value = '';
      if (stepDelayRef.current) stepDelayRef.current.value = '0';
      if (stepRequiredRef.current) stepRequiredRef.current.checked = true;
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Plus size={16} style={{ verticalAlign: '-3px' }} /> Add New Step</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Step Message</label>
            <textarea 
              className="form-textarea" 
              rows="4"
              ref={stepMsgRef}
              defaultValue=""
              placeholder="Enter the message for this step..."
            />
            <div className="form-hint">
              Use {'{Name}'}, {'{ChurchName}'}, {'{PastorName}'} for personalisation
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Field to Capture</label>
            <input 
              className="form-input" 
              ref={stepFieldRef}
              defaultValue=""
              placeholder="e.g. Full Name, Date of Birth"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Delay (seconds)</label>
            <input 
              className="form-input" 
              type="number"
              ref={stepDelayRef}
              defaultValue="0"
              placeholder="0 for immediate"
              min="0"
              max="60"
            />
            <div className="form-hint">Delay before sending this message</div>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input 
                type="checkbox" 
                ref={stepRequiredRef}
                defaultChecked={true}
              />
              Required field
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={() => {
              const msg = stepMsgRef.current?.value?.trim() || '';
              const field = stepFieldRef.current?.value?.trim() || '';
              const delay = parseInt(stepDelayRef.current?.value) || 0;
              const required = stepRequiredRef.current?.checked || false;
              
              if (!msg) {
                showToastMessage('Please enter a message', 'error');
                return;
              }
              
              const newStep = {
                id: flowSteps.length + 1,
                step: flowSteps.length + 1,
                msg,
                field: field || 'Custom field',
                required,
                delay: delay || 0,
                is_active: true
              };
              setFlowSteps([...flowSteps, newStep]);
              showToastMessage(`✅ Step ${newStep.step} added!`, 'success');
              closeModal();
            }}
          >
            <Plus size={16} style={{ verticalAlign: '-3px' }} /> Add Step
          </button>
        </div>
      </div>
    );
  };

  const handleEditStep = (step) => {
    setEditingStepId(step.id);
    
    setTimeout(() => {
      if (stepMsgRef.current) stepMsgRef.current.value = step.msg;
      if (stepFieldRef.current) stepFieldRef.current.value = step.field;
      if (stepDelayRef.current) stepDelayRef.current.value = step.delay || 0;
      if (stepRequiredRef.current) stepRequiredRef.current.checked = step.required !== false;
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit Step {step.step}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Step Message</label>
            <textarea 
              className="form-textarea" 
              rows="4"
              ref={stepMsgRef}
              defaultValue=""
            />
            <div className="form-hint">
              Use {'{Name}'}, {'{ChurchName}'}, {'{PastorName}'} for personalisation
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Field to Capture</label>
            <input 
              className="form-input" 
              ref={stepFieldRef}
              defaultValue=""
              placeholder="e.g. Full Name, Date of Birth"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Delay (seconds)</label>
            <input 
              className="form-input" 
              type="number"
              ref={stepDelayRef}
              defaultValue="0"
              placeholder="0 for immediate"
              min="0"
              max="60"
            />
            <div className="form-hint">Delay before sending this message</div>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input 
                type="checkbox" 
                ref={stepRequiredRef}
                defaultChecked={true}
              />
              Required field
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={() => {
              const msg = stepMsgRef.current?.value?.trim() || '';
              const field = stepFieldRef.current?.value?.trim() || '';
              const delay = parseInt(stepDelayRef.current?.value) || 0;
              const required = stepRequiredRef.current?.checked || false;
              
              if (!msg) {
                showToastMessage('Please enter a message', 'error');
                return;
              }
              
              setFlowSteps(flowSteps.map(s => 
                s.id === step.id 
                  ? { 
                      ...s, 
                      msg,
                      field: field || 'Custom field',
                      delay: delay || 0,
                      required
                    }
                  : s
              ));
              showToastMessage(`✅ Step ${step.step} updated!`, 'success');
              setEditingStepId(null);
              closeModal();
            }}
          >
<Save size={16} style={{ verticalAlign: '-3px' }} /> Save Step
          </button>
        </div>
      </div>
    );
  };

  const handleDeleteStep = (step) => {
    if (flowSteps.length <= 1) {
      showToastMessage('⚠️ Cannot delete the last step', 'error');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete Step</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete Step {step.step}?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
<AlertTriangle size={16} style={{ verticalAlign: '-3px' }} /> This will remove the step from the registration flow.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            onClick={() => {
              const updatedSteps = flowSteps
                .filter(s => s.id !== step.id)
                .map((s, index) => ({ ...s, step: index + 1 }));
              setFlowSteps(updatedSteps);
              showToastMessage(`🗑️ Step ${step.step} deleted`, 'error');
              closeModal();
            }}
          >
            Delete Step
          </button>
        </div>
      </div>
    );
  };

  const handleMoveStepUp = (step) => {
    const index = flowSteps.findIndex(s => s.id === step.id);
    if (index === 0) return;
    
    const newSteps = [...flowSteps];
    [newSteps[index], newSteps[index - 1]] = [newSteps[index - 1], newSteps[index]];
    const renumberedSteps = newSteps.map((s, i) => ({ ...s, step: i + 1 }));
    setFlowSteps(renumberedSteps);
    showToastMessage('⬆️ Step moved up', 'info');
  };

  const handleMoveStepDown = (step) => {
    const index = flowSteps.findIndex(s => s.id === step.id);
    if (index === flowSteps.length - 1) return;
    
    const newSteps = [...flowSteps];
    [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
    const renumberedSteps = newSteps.map((s, i) => ({ ...s, step: i + 1 }));
    setFlowSteps(renumberedSteps);
    showToastMessage('⬇️ Step moved down', 'info');
  };

  // ==================== KEYWORDS ====================

  const handleAddKeyword = () => {
    setTimeout(() => {
      if (keywordInputRef.current) keywordInputRef.current.value = '';
      if (keywordDescRef.current) keywordDescRef.current.value = '';
      if (keywordActiveRef.current) keywordActiveRef.current.checked = true;
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Plus size={16} style={{ verticalAlign: '-3px' }} /> Add Keyword</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Keyword</label>
            <input 
              className="form-input" 
              ref={keywordInputRef}
              defaultValue=""
              placeholder="e.g. PRAYER"
            />
            <div className="form-hint">The word users will text to trigger this action</div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input 
              className="form-input" 
              ref={keywordDescRef}
              defaultValue=""
              placeholder="What this keyword does"
            />
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input 
                type="checkbox" 
                ref={keywordActiveRef}
                defaultChecked={true}
              />
              Active
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={() => {
              const keyword = keywordInputRef.current?.value?.trim() || '';
              const description = keywordDescRef.current?.value?.trim() || '';
              const active = keywordActiveRef.current?.checked || false;
              
              if (!keyword) {
                showToastMessage('Please enter a keyword', 'error');
                return;
              }
              if (!description) {
                showToastMessage('Please enter a description', 'error');
                return;
              }
              
              const newKeyword = {
                keyword: keyword.toUpperCase(),
                description: description,
                active: active,
                action_type: 'reply'
              };
              setKeywords([...keywords, newKeyword]);
              showToastMessage(`✅ "${keyword}" keyword added!`, 'success');
              closeModal();
            }}
          >
            Add Keyword
          </button>
        </div>
      </div>
    );
  };

  const handleEditKeyword = (keyword) => {
    setEditingKeywordId(keyword.id);
    
    setTimeout(() => {
      if (keywordInputRef.current) keywordInputRef.current.value = keyword.keyword;
      if (keywordDescRef.current) keywordDescRef.current.value = keyword.description;
      if (keywordActiveRef.current) keywordActiveRef.current.checked = keyword.active;
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit Keyword</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Keyword</label>
            <input 
              className="form-input" 
              ref={keywordInputRef}
              defaultValue=""
            />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input 
              className="form-input" 
              ref={keywordDescRef}
              defaultValue=""
            />
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input 
                type="checkbox" 
                ref={keywordActiveRef}
                defaultChecked={true}
              />
              Active
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={() => {
              const kw = keywordInputRef.current?.value?.trim() || '';
              const desc = keywordDescRef.current?.value?.trim() || '';
              const active = keywordActiveRef.current?.checked || false;
              
              if (!kw) {
                showToastMessage('Please enter a keyword', 'error');
                return;
              }
              if (!desc) {
                showToastMessage('Please enter a description', 'error');
                return;
              }
              
              setKeywords(keywords.map(k => 
                k.id === keyword.id 
                  ? { ...k, keyword: kw.toUpperCase(), description: desc, active }
                  : k
              ));
              showToastMessage(`✅ "${kw}" keyword updated!`, 'success');
              setEditingKeywordId(null);
              closeModal();
            }}
          >
            <Save size={16} style={{ verticalAlign: '-3px' }} /> Save Keyword
          </button>
        </div>
      </div>
    );
  };

  const handleDeleteKeyword = (keyword) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete Keyword</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete the keyword <strong>{keyword.keyword}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
<AlertTriangle size={16} style={{ verticalAlign: '-3px' }} /> This will remove the keyword and its associated action.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            onClick={() => {
              setKeywords(keywords.filter(k => k.id !== keyword.id));
              showToastMessage(`🗑️ "${keyword.keyword}" keyword deleted`, 'error');
              closeModal();
            }}
          >
            Delete Keyword
          </button>
        </div>
      </div>
    );
  };

  // ==================== SAVE FLOW ====================

  const handleSaveFlow = async () => {
    const invalidSteps = flowSteps.filter(s => !s.msg?.trim());
    if (invalidSteps.length > 0) {
      showToastMessage(`⚠️ ${invalidSteps.length} step(s) have empty messages`, 'error');
      return;
    }
    
    try {
      // Convert local flow steps to API format
      const flowsToSave = flowSteps.map((s, index) => ({
        step: index + 1,
        msg: s.msg,
        field: s.field === 'None — trigger' ? null : s.field,
        required: s.required !== false,
        delay: s.delay || 0,
        is_active: s.is_active !== false
      }));
      
      const result = await dispatch(saveShortcodeFlow({ flows: flowsToSave })).unwrap();
      if (result.success) {
        showToastMessage('✅ Registration flow saved successfully!', 'success');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to save registration flow', 'error');
    }
  };

  // ==================== SAVE KEYWORDS ====================

  const handleSaveKeywords = async () => {
    try {
      const keywordsToSave = keywords.map(k => ({
        keyword: k.keyword,
        description: k.description,
        action_type: k.action_type || 'reply',
        response_message: k.response_message || null,
        active: k.active !== false
      }));
      
      const result = await dispatch(saveShortcodeKeywords({ keywords: keywordsToSave })).unwrap();
      if (result.success) {
        showToastMessage('✅ Keywords saved successfully!', 'success');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to save keywords', 'error');
    }
  };

  // ==================== PREVIEW FLOW ====================

  const handlePreviewFlow = () => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Eye size={16} style={{ verticalAlign: '-3px' }} /> Preview Registration Flow</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ 
            maxWidth: '400px', 
            margin: '0 auto',
            background: '#1a1a2e',
            borderRadius: '12px',
            padding: '16px'
          }}>
            <div style={{ 
              background: '#2d2d44',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '12px'
            }}>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,.4)', marginBottom: '4px' }}>
                You → {shortcodeNumber}
              </div>
              <div style={{ fontSize: '14px', color: 'rgba(255,255,255,.85)' }}>
                {registrationKeyword}
              </div>
            </div>
            
            {flowSteps.map((step, index) => (
              <div key={step.id} style={{ 
                background: index % 2 === 0 ? '#2d2d44' : '#1f1f38',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '8px'
              }}>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,.4)', marginBottom: '4px' }}>
                  {index % 2 === 0 ? `${shortcodeNumber} → You` : 'You → 40404'}
                </div>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,.85)' }}>
                  {step.msg.replace(/{Name}/g, 'Emmanuel').replace(/{ChurchName}/g, 'Grace Chapel')}
                </div>
                {step.delay > 0 && (
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,.3)', marginTop: '4px' }}>
                    ⏱ Delay: {step.delay}s
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  // ==================== TOGGLE FLOW ACTIVE ====================

  const toggleFlowActive = async () => {
    try {
      const result = await dispatch(updateShortcodeSettings({
        is_active: !isFlowActive
      })).unwrap();
      
      if (result.success) {
        setIsFlowActive(!isFlowActive);
        showToastMessage(isFlowActive ? '⏸️ Flow paused' : '▶️ Flow resumed', 'info');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to toggle flow status', 'error');
    }
  };

  // ==================== EXPORT ====================

  const handleExportData = async () => {
    try {
      const result = await dispatch(exportShortcodeData()).unwrap();
      if (result.success) {
        showToastMessage('📥 Shortcode data exported successfully!', 'success');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to export data', 'error');
    }
  };

  // ==================== RESET TO DEFAULT ====================

  const handleResetToDefault = () => {
    const defaultFlow = [
      { id: 1, step: 1, msg: 'Welcome to {ChurchName}! 🙏 We\'re glad you reached out. Please reply with your FULL NAME to begin registration.', field: 'None — trigger', required: true, delay: 0 },
      { id: 2, step: 2, msg: 'Thank you {Name}! Please reply with your DATE OF BIRTH in the format DD/MM/YYYY (e.g. 15/07/1990)', field: 'Full Name', required: true, delay: 0 },
      { id: 3, step: 3, msg: 'Got it! Which DEPARTMENT would you like to join? Reply: 1=Youth, 2=Women\'s Ministry, 3=Choir, 4=Ushers, 5=Prayer Team, 6=General', field: 'Date of Birth', required: true, delay: 0 },
      { id: 4, step: 4, msg: 'You\'re all set, {Name}! 🎉 Welcome to {ChurchName}! Our next Sunday service is at 8:30 AM. We can\'t wait to see you! God bless you 🙏', field: 'Department choice', required: false, delay: 0 }
    ];
    setFlowSteps(defaultFlow);
    showToastMessage('🔄 Flow reset to default', 'info');
  };

  // ==================== RENDER ====================

  if (!isInitialized && loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}><Loader2 size={20} /></div>
        <div style={{ color: 'var(--text-light)' }}>Loading shortcode configuration...</div>
      </div>
    );
  }

  const activeKeywordCount = keywords.filter(k => k.active).length;

  return (
    <>
      <div className="grid-2" style={{ marginBottom: '20px' }}>
        <div className="card">
          <div className="card-title" style={{ marginBottom: '16px' }}>Your Church Shortcode</div>
          <div className="shortcode-box" style={{ 
            background: 'linear-gradient(135deg, var(--navy), var(--navy-light))', 
            borderRadius: '12px', 
            padding: '20px 24px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            color: 'white',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            <div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,.5)', marginBottom: '4px' }}>Shortcode Number</div>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '32px', fontWeight: '500', color: 'var(--gold-light)', letterSpacing: '.05em' }}>
                {shortcodeNumber}
              </div>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,.4)', marginTop: '4px' }}>Powered by Africa's Talking</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,.5)', marginBottom: '4px' }}>Registration Keyword</div>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '18px', color: 'var(--gold)' }}>
                {registrationKeyword}
              </div>
            </div>
          </div>
          
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button 
              className="btn btn-ghost btn-sm" 
              onClick={handleEditShortcode}
              disabled={loading}
            >
              <Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit Shortcode Settings
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleExportData}
              disabled={loading}
            >
              <Download size={16} style={{ verticalAlign: '-3px' }} /> Export Data
            </button>
          </div>

          <div style={{ marginTop: '16px', padding: '14px', background: 'var(--gold-pale)', border: '1px solid #f0dfa0', borderRadius: '10px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)', marginBottom: '6px' }}><Smartphone size={14} style={{ verticalAlign: '-2px' }} /> How new members register:</div>
            <div style={{ fontSize: '13px', color: 'var(--text-mid)', lineHeight: '1.8' }}>
              1. Text <strong>{registrationKeyword}</strong> to <strong>{shortcodeNumber}</strong><br />
              2. System asks for Full Name<br />
              3. System asks for Date of Birth<br />
              4. System asks for Department<br />
              5. Member added & welcome SMS sent <CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} />
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-title" style={{ marginBottom: '16px' }}>
            Keyword Commands
            <span style={{ fontSize: '12px', fontWeight: '400', color: 'var(--text-light)', marginLeft: '8px' }}>
              · {activeKeywordCount} active
            </span>
          </div>
          {keywords.map(k => (
            <div key={k.id} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px', 
              padding: '10px 12px', 
              background: k.color || 'var(--blue-bg)', 
              borderRadius: '8px', 
              marginBottom: '8px',
              flexWrap: 'wrap'
            }}>
              <div style={{ 
                fontFamily: 'JetBrains Mono, monospace', 
                fontSize: '13px', 
                fontWeight: '700', 
                color: k.textColor || 'var(--blue)', 
                minWidth: '100px' 
              }}>
                {k.keyword}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-mid)', flex: 1 }}>
                {k.description}
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button 
                  className="btn btn-ghost btn-sm btn-icon" 
                  onClick={() => handleEditKeyword(k)}
                  title="Edit"
                  disabled={loading}
                >
                  <Pencil size={16} />
                </button>
                <button
                  className="btn btn-ghost btn-sm btn-icon"
                  onClick={() => handleDeleteKeyword(k)}
                  title="Delete"
                  style={{ color: 'var(--red)' }}
                  disabled={loading}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
            <button 
              className="btn btn-ghost btn-sm" 
              onClick={handleAddKeyword}
              disabled={loading}
            >
              + Add Keyword
            </button>
            <button 
              className="btn btn-gold btn-sm" 
              onClick={handleSaveKeywords}
              disabled={isSubmitting}
            >
<Save size={16} style={{ verticalAlign: '-3px' }} /> Save Keywords
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Registration Flow Builder</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <span className={`badge ${isFlowActive ? 'badge-green' : 'badge-amber'}`}>
              {isFlowActive ? 'Active' : 'Paused'}
            </span>
            <button 
              className="btn btn-ghost btn-sm" 
              onClick={toggleFlowActive}
              disabled={isSubmitting}
            >
              {isFlowActive
                ? <><Pause size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Pause</>
                : <><Play size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Resume</>}
            </button>
            <button 
              className="btn btn-ghost btn-sm" 
              onClick={handlePreviewFlow}
              disabled={loading}
            >
<Eye size={16} style={{ verticalAlign: '-3px' }} /> Preview
            </button>
          </div>
        </div>
        <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
          Customise the SMS conversation new members see when they text {registrationKeyword}:
        </div>
        
        {flowSteps.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-light)' }}>
            <div style={{ marginBottom: '8px' }}><FileText size={24} /></div>
            <div>No steps configured yet</div>
            <div style={{ fontSize: '12px', marginTop: '4px' }}>Click "Add Step" to create your registration flow</div>
          </div>
        ) : (
          flowSteps.map((s, index) => (
            <div key={s.id} style={{ 
              display: 'flex', 
              gap: '14px', 
              marginBottom: '16px',
              opacity: isFlowActive && s.is_active !== false ? 1 : 0.6
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ 
                  width: '30px', 
                  height: '30px', 
                  borderRadius: '50%', 
                  background: 'var(--navy)', 
                  color: 'var(--gold-light)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: '12px', 
                  fontWeight: '700', 
                  flexShrink: 0 
                }}>
                  {s.step}
                </div>
                {s.step < flowSteps.length && (
                  <div style={{ flex: 1, width: '2px', background: 'var(--border)', margin: '4px 0' }} />
                )}
              </div>
              <div style={{ flex: 1, paddingBottom: '8px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '4px',
                  marginBottom: '4px'
                }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                    Captures: <strong>{s.field}</strong>
                    {s.delay > 0 && ` · ⏱ ${s.delay}s delay`}
                    {s.required && <span style={{ color: 'var(--red)', marginLeft: '4px' }}>· Required</span>}
                  </div>
                  <div style={{ display: 'flex', gap: '2px' }}>
                    <button 
                      className="btn btn-ghost btn-sm btn-icon" 
                      onClick={() => handleMoveStepUp(s)}
                      title="Move Up"
                      disabled={index === 0 || loading}
                      style={{ opacity: index === 0 ? 0.3 : 1 }}
                    >
                      ↑
                    </button>
                    <button 
                      className="btn btn-ghost btn-sm btn-icon" 
                      onClick={() => handleMoveStepDown(s)}
                      title="Move Down"
                      disabled={index === flowSteps.length - 1 || loading}
                      style={{ opacity: index === flowSteps.length - 1 ? 0.3 : 1 }}
                    >
                      ↓
                    </button>
                    <button 
                      className="btn btn-ghost btn-sm btn-icon" 
                      onClick={() => handleEditStep(s)}
                      title="Edit"
                      disabled={loading}
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      className="btn btn-ghost btn-sm btn-icon"
                      onClick={() => handleDeleteStep(s)}
                      title="Delete"
                      style={{ color: 'var(--red)' }}
                      disabled={loading}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <div style={{ 
                  background: 'var(--cream)', 
                  border: '1px solid var(--border)', 
                  borderRadius: '8px', 
                  padding: '10px 12px', 
                  fontSize: '13px', 
                  color: 'var(--text)' 
                }}>
                  {s.msg}
                </div>
              </div>
            </div>
          ))
        )}
        
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '8px' }}>
          <button 
            className="btn btn-ghost btn-sm" 
            onClick={handleAddStep}
            disabled={loading}
          >
            <Plus size={16} style={{ verticalAlign: '-3px' }} /> Add Step
          </button>
          <button
            className="btn btn-gold btn-sm"
            onClick={handleSaveFlow}
            disabled={isSubmitting}
          >
            <Save size={16} style={{ verticalAlign: '-3px' }} /> Save Flow
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleResetToDefault}
            disabled={loading}
          >
            <RefreshCw size={16} style={{ verticalAlign: '-3px' }} /> Reset to Default
          </button>
        </div>
      </div>

      <style>{`
        .badge {
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          flex-wrap: wrap;
          gap: 10px;
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .form-hint {
          font-size: 11px;
          color: var(--text-light);
          margin-top: 4px;
        }
        @media (max-width: 768px) {
          .grid-2 {
            grid-template-columns: 1fr !important;
          }
          .shortcode-box {
            flex-direction: column;
            text-align: center !important;
          }
          .shortcode-box > div:last-child {
            text-align: center !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
          }
        }
      `}</style>
    </>
  );
};

export default ShortcodeSetup;